// Author: Ahsan Saleem
/**
 * Living surfaces — the primitives that make BloomOS feel operated, not loaded.
 *
 * Motion here is FUNCTIONAL and HONEST: a number eases toward a new value and flashes
 * ONLY when the underlying datum actually changed, so the eye is drawn to what moved —
 * never to fabricated movement. Freshness ticks so an operator can see the system is
 * live. Everything degrades to static under prefers-reduced-motion.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

export function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(() => typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduce(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return reduce;
}

/** A ticking clock — re-renders every intervalMs so relative times stay current. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Eases a number toward `target` when it changes (easeOutCubic); static if reduced. */
export function useAnimatedNumber(target: number, durationMs = 650): number {
  const reduce = usePrefersReducedMotion();
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(0);
  useEffect(() => {
    if (reduce) { fromRef.current = target; setDisplay(target); return; }
    const from = fromRef.current;
    if (from === target) return;
    let start = 0;
    const step = (ts: number) => {
      if (!start) start = ts;
      const t = Math.min(1, (ts - start) / durationMs);
      const val = from + (target - from) * (1 - Math.pow(1 - t, 3));
      fromRef.current = val;
      setDisplay(val);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, durationMs, reduce]);
  return reduce ? target : display;
}

/**
 * A number that counts to its new value and flashes in the direction it moved. Renders
 * a placeholder when the value is absent (never animates from a fake zero into real data).
 */
export function LiveNumber({ value, decimals = 0, placeholder = '—', className = '' }: {
  value: number | null | undefined; decimals?: number; placeholder?: ReactNode; className?: string;
}) {
  const valid = value != null && Number.isFinite(value);
  const animated = useAnimatedNumber(valid ? (value as number) : 0);
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  const prev = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const p = prev.current;
    prev.current = value;
    if (p != null && value != null && p !== value) {
      setFlash(value > p ? 'up' : 'down');
      const id = setTimeout(() => setFlash(null), 1000);
      return () => clearTimeout(id);
    }
  }, [value]);
  if (!valid) return <span className={`live-num tnum ${className}`}>{placeholder}</span>;
  return <span className={`live-num tnum ${className}`} data-flash={flash ?? undefined}>{animated.toFixed(decimals)}</span>;
}

/** "· 3s ago" — a live relative-time that ticks every second. */
export function Freshness({ at, prefix = 'updated', className = '' }: { at?: number; prefix?: string; className?: string }) {
  const now = useNow(1000);
  if (!at) return null;
  const s = Math.max(0, Math.round((now - at) / 1000));
  const rel = s < 3 ? 'just now' : s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)}m ago` : `${Math.floor(s / 3600)}h ago`;
  return <span className={`live-fresh ${className}`}>{prefix ? `${prefix} ` : ''}{rel}</span>;
}
